"use client";

import { useLocale, useTranslations } from "next-intl";

import { Link, usePathname } from "@/i18n/navigation";

const SITE_URL = "https://maidijital.com";

const LABEL_MAP: Record<string, string> = {
  "/services": "services",
  "/services/development": "development",
  "/services/development/mobile-web-apps": "mobileWebApps",
  "/services/development/ecommerce": "ecommerce",
  "/services/design": "design",
  "/services/design/brand-identity": "brandIdentity",
  "/services/design/ui-ux": "uiUx",
  "/services/design/motion": "motion",
  "/services/growth": "growth",
  "/services/growth/marketing-seo": "marketingSeo",
  "/services/growth/strategy": "strategy",
  "/products": "products",
  "/products/namaz-plus": "namazPlus",
  "/products/mavi-ilan": "maviIlan",
  "/products/servisciler": "servisciler",
  "/work": "work",
  "/work/fintech-platform": "fintechPlatform",
  "/work/ecommerce-redesign": "ecommerceRedesign",
  "/work/healthcare-dashboard": "healthcareDashboard",
  "/work/saas-onboarding": "saasOnboarding",
  "/work/brand-identity": "workBrandIdentity",
  "/studio": "studio",
  "/contact": "contact",
};

type Crumb = { label: string; href: string; isCurrent: boolean };

export function Breadcrumb() {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("breadcrumb");

  if (!pathname || pathname === "/") return null;

  const segments = pathname.split("/").filter(Boolean);
  const crumbs: Crumb[] = [
    { label: t("home"), href: "/", isCurrent: false },
  ];

  let accum = "";
  for (let i = 0; i < segments.length; i++) {
    accum += "/" + segments[i];
    const key = LABEL_MAP[accum];
    if (!key) continue;
    const isLast = i === segments.length - 1;
    crumbs.push({ label: t(key), href: accum, isCurrent: isLast });
  }

  if (crumbs.length < 2) return null;

  const toAbsolute = (href: string) =>
    href === "/" ? `${SITE_URL}/${locale}` : `${SITE_URL}/${locale}${href}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      item: toAbsolute(c.href),
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <nav aria-label="Breadcrumb" className="mb-6">
        <ol
          className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase text-fg3"
          style={{ letterSpacing: "0.18em" }}
        >
          {crumbs.map((c, i) => {
            const isMiddle = i > 0 && !c.isCurrent;
            const truncateCls = isMiddle
              ? "inline-block max-w-[88px] truncate align-bottom sm:max-w-none"
              : "";
            return (
              <li
                key={c.href + i}
                className="inline-flex items-center gap-x-2"
              >
                {c.isCurrent ? (
                  <span
                    aria-current="page"
                    className={`text-fg1 ${truncateCls}`}
                  >
                    {c.label}
                  </span>
                ) : (
                  <Link
                    href={c.href}
                    className={`text-fg3 transition-colors hover:text-brand-azure ${truncateCls}`}
                    style={{
                      transitionDuration: "220ms",
                      transitionTimingFunction: "var(--ease-out)",
                    }}
                  >
                    {c.label}
                  </Link>
                )}
                {!c.isCurrent ? (
                  <span aria-hidden className="text-fg4 select-none">
                    ›
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
